import React from "react";
import ReusableDataGridDashboard from "./ReusableDataGridDashboard";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import PieChartShow from "./PieChartShow";
import ModernAreaChart from "./ModernAreaChart";

const DashboardContent = ({ adminUsers, fetchAdminAllUsers }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Responsive width calculation
  const getChartWidth = () => {
    if (isMobile) {
      return Math.max(adminUsers.length * 60, 300); // Mobile: 60px per user, min 300px
    } else if (isTablet) {
      return Math.max(adminUsers.length * 80, 400); // Tablet: 80px per user, min 400px
    } else {
      return Math.max(adminUsers.length * 100, 500); // Desktop: 100px per user, min 500px
    }
  };

  return (
    <Box
      className="pb-16"
      sx={{
        mt: { xs: 8, sm: 4, md: 2 },
      }}
    >
      {/* Charts above DataGrid */}
      <Grid container spacing={3}>
        {/* Bar Chart */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{ 
              height: { xs: 450, md: 400 },
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: 3,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
              }
            }}
          >
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                p: { xs: 2, md: 3 }
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                component="h2"
                gutterBottom
                textAlign="center"
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  letterSpacing: '-0.5px'
                }}
              >
                Total License Distribution
              </Typography>

              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  flexGrow: 1,
                  position: 'relative',
                  '& .recharts-bar': {
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                  }
                }}
              >
                <Box
                  sx={{
                    width: getChartWidth(),
                    height: "100%",
                    minHeight: 0,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={adminUsers}
                      margin={{
                        top: 10,
                        right: isMobile ? 10 : 20,
                        left: isMobile ? 5 : 10,
                        bottom: 30,
                      }}
                      barGap={isMobile ? 5 : 10}
                      barCategoryGap={isMobile ? 10 : 20}
                      onMouseMove={(e) => {
                        // Prevent flickering by handling mouse movement
                        if (e && e.activeLabel && e.target && e.target.style) {
                          e.target.style.cursor = 'pointer';
                        }
                      }}
                      onMouseLeave={() => {
                        // Reset cursor when leaving chart area
                        if (document && document.body) {
                          document.body.style.cursor = 'default';
                        }
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                        opacity={0.3}
                        strokeWidth={0.5}
                      />
                      <XAxis
                        dataKey="USER_NAME"
                        interval={0}
                        angle={isMobile ? -60 : -45}
                        textAnchor="end"
                        height={isMobile ? 60 : 70}
                        tick={{
                          fontSize: isMobile ? 10 : 12,
                          fill: theme.palette.text.secondary,
                          fontWeight: 500
                        }}
                        axisLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
                        tickLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
                      />
                      <YAxis
                        label={{ 
                          value: 'Number of Licenses', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { 
                            textAnchor: 'middle',
                            fontSize: isMobile ? 10 : 12,
                            fontWeight: 600,
                            fill: theme.palette.text.primary
                          }
                        }}
                        tick={{
                          fontSize: isMobile ? 9 : 11,
                          fill: theme.palette.text.secondary,
                          fontWeight: 500
                        }}
                        axisLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
                        tickLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
                      />
                      <Tooltip 
                        contentStyle={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: 8,
                          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                          fontSize: isMobile ? '12px' : '14px'
                        }}
                        formatter={(value, name) => [`${value} Licenses`, 'Total Licenses']}
                        cursor={{ fill: 'rgba(25, 118, 210, 0.1)' }}
                        isAnimationActive={false}
                      />
                      <Legend 
                        verticalAlign="top" 
                        height={isMobile ? 30 : 36}
                        wrapperStyle={{
                          paddingBottom: isMobile ? 5 : 10,
                          fontSize: isMobile ? 10 : 12,
                          fontWeight: 600
                        }}
                      />
                      <Bar
                        dataKey="TOTAL_LICENSES"
                        name="Total Licenses"
                        fill="url(#licenseGradient)"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={isMobile ? 30 : 50}
                        onMouseEnter={(e) => {
                          if (e && e.target && e.target.style) {
                            e.target.style.cursor = 'pointer';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (e && e.target && e.target.style) {
                            e.target.style.cursor = 'default';
                          }
                        }}
                        isAnimationActive={true}
                        animationDuration={300}
                        animationEasing="ease-out"
                      />
                      <defs>
                        <linearGradient id="licenseGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" />
                          <stop offset="100%" stopColor="#059669" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} md={4}>
          <PieChartShow adminUsers={adminUsers} />
        </Grid>

        {/* Modern Area Chart */}
        <Grid item xs={12} md={4}>
          <ModernAreaChart adminUsers={adminUsers} />
        </Grid>
      </Grid>

      {/* Data Grid below charts */}
      <Box mt={6}>
        <ReusableDataGridDashboard
          rows={adminUsers}
          fetchAdminAllUsers={fetchAdminAllUsers}
        />
      </Box>
    </Box>
  );
};

export default DashboardContent;
